import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { staggerContainer, staggerItem, fadeInUp } from '../../utils/animation';
import { STATS, EXPERIENCE_ITEMS, TECHNOLOGIES } from '../../data/constants';
import './Skills.css';

const CountUp: React.FC<{
  end: number;
  duration: number;
  format?: 'number' | 'percentage' | 'compact';
  suffix?: string;
}> = ({ end, duration, format = 'number', suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number;
    let frame: number;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / (duration * 1000), 1);
      let val = Math.floor(progress * end);
      if (format === 'compact' && end >= 1000000) val = Math.floor(progress * (end / 1000000));
      setCount(val);
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [end, duration, format]);
  const fmt = (n: number) => (format === 'compact' && end >= 1000000 ? `${n}M` : n.toString());
  return <span>{fmt(count)}{suffix}</span>;
};

export const Skills: React.FC = () => {
  const { ref, inView } = useIntersectionObserver({ threshold: 0.06, triggerOnce: true });

  return (
    <section id="skills" className="skills section section--dark" ref={ref}>
      <div className="container">
        <div className="section__header">
          <span className="section__subtitle">EXPERIENCE & EXPERTISE</span>
          <h2 className="section__title">7 Years of Engineering</h2>
          <p className="section__description">
            From Software Engineer Trainee to Solutions Architect - architecting GenAI pipelines,
            petabyte Lakehouses, and cloud-native systems serving 1M+ users across 9 Asian markets.
          </p>
        </div>

        {/* SVG gradient def */}
        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366F1" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
        </svg>

        {/* Stats row */}
        <motion.div
          className="skills__stats-row"
          variants={staggerContainer}
          initial="initial"
          animate={inView ? 'animate' : 'initial'}
        >
          {STATS.map((stat, i) => {
            const progress = stat.format === 'compact'
              ? Math.min(stat.number / 1000000, 1)
              : Math.min(stat.number / (stat.format === 'percentage' ? 100 : 10), 1);
            const r = 38;
            const circ = +(2 * Math.PI * r).toFixed(2);
            return (
              <motion.div key={i} className="skills__stat-card" variants={staggerItem}>
                <div className="skills__ring-wrap">
                  <svg viewBox="0 0 100 100" className="skills__ring-svg">
                    <circle cx="50" cy="50" r={r} className="skills__ring-bg" />
                    <motion.circle
                      cx="50" cy="50" r={r}
                      className="skills__ring-fg"
                      strokeDasharray={`${circ} ${circ}`}
                      initial={{ strokeDashoffset: circ }}
                      animate={inView ? { strokeDashoffset: circ * (1 - progress) } : { strokeDashoffset: circ }}
                      transition={{ duration: 2.2, delay: 0.3 + i * 0.25, ease: [0.22,1,0.36,1] }}
                      style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                    />
                  </svg>
                  <div className="skills__ring-value">
                    {inView && <CountUp end={stat.number} duration={2} format={stat.format} suffix={stat.suffix} />}
                  </div>
                </div>
                <span className="skills__stat-label">{stat.label}</span>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Work History */}
        <div className="skills__timeline-section">
          <h3 className="skills__section-heading">Work History</h3>
          <div className="skills__timeline">
            {EXPERIENCE_ITEMS.map((item, i) => (
              <motion.div
                key={i}
                className="exp__item"
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: i * 0.12, ease: [0.22,1,0.36,1] }}
              >
                {/* Left: role info */}
                <div className="exp__left">
                  <div className="exp__period-pill">{item.period}</div>
                  <h4 className="exp__role">{item.position}</h4>
                  <div className="exp__company">{item.company}</div>
                </div>

                {/* Right: description + bullets */}
                <div className="exp__right">
                  <p className="exp__desc">{item.description}</p>
                  <ul className="exp__bullets">
                    {item.highlights.map((h, hi) => (
                      <li key={hi} className="exp__bullet">
                        <span className="exp__bullet-mark" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Tech stack */}
        <motion.div
          className="skills__tech-section"
          variants={fadeInUp}
          initial="initial"
          animate={inView ? 'animate' : 'initial'}
        >
          <h3 className="skills__section-heading">Tech Stack</h3>
          <motion.div
            className="skills__tech-grid"
            variants={staggerContainer}
            initial="initial"
            animate={inView ? 'animate' : 'initial'}
          >
            {TECHNOLOGIES.map((tech, i) => (
              <motion.span
                key={i}
                className="skills__tech-chip"
                variants={staggerItem}
                whileHover={{ y: -2, borderColor: 'rgba(99,102,241,0.5)', color: 'var(--color-primary-light)', transition: { duration: 0.15 } }}
              >
                {tech}
              </motion.span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
